import { useEffect, useRef, useState } from "react";

const FLUID_JS_URL = "https://cdn.fluidplayer.com/v3/current/fluidplayer.min.js";

const loadFluidAsset = (tagName, attrName, url) => {
  const existing = document.querySelector(`${tagName}[${attrName}="${url}"]`);
  if (existing) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const element = document.createElement(tagName);
    element[attrName] = url;

    if (tagName === "script") {
      element.async = true;
    } else if (tagName === "link") {
      element.rel = "stylesheet";
    }

    element.onload = resolve;
    element.onerror = reject;
    document.head.appendChild(element);
  });
};

const getSourceType = (url = "") => {
  if (url.includes(".m3u8")) return "application/x-mpegURL";
  if (url.includes(".webm")) return "video/webm";
  return "video/mp4";
};

export default function FluidVideoPlayer({ videoUrl, adUrl, onAdFinished, forceMobileSlot = false }) {
  const videoIdRef = useRef(`fluid-video-${Math.random().toString(36).slice(2)}`);
  const playerRef = useRef(null);
  const adDoneRef = useRef(false);
  const timersRef = useRef([]);
  const [canSkipAd, setCanSkipAd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adDoneRef.current = false;
    setCanSkipAd(false);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const finishAdAttempt = (reason) => {
      if (adDoneRef.current) return;
      adDoneRef.current = true;
      if (reason) console.warn(reason);
      onAdFinished?.();
    };

    const setup = async () => {
      try {
        await loadFluidAsset("script", "src", FLUID_JS_URL);

        if (cancelled || !window.fluidPlayer) return;

        if (playerRef.current?.destroy) {
          try { playerRef.current.destroy(); } catch (_) { }
        }

        playerRef.current = window.fluidPlayer(videoIdRef.current, {
          layoutControls: {
            fillToContainer: true,
            autoPlay: false,
            playButtonShowing: true,
            allowTheatre: false,
            playbackRateEnabled: true,
          },
          hls: {
            overrideNative: true,
          },
          vastOptions: adUrl
            ? {
              vastTimeout: 10000,
              adClickable: false,
              adCTAText: false,
              showPlayButton: true,
              maxAllowedVastTagRedirects: 5,
              adList: [
                {
                  roll: "preRoll",
                  vastTag: adUrl,
                  adClickable: false,
                },
              ],
              vastAdvanced: {
                vastLoadedCallback: () => console.log("Fluid Player: VAST loaded"),
                noVastVideoCallback: () => {
                  finishAdAttempt("Fluid Player: no VAST video");
                },
                vastVideoSkippedCallback: () => finishAdAttempt(),
                vastVideoEndedCallback: () => finishAdAttempt(),
                vastVideoEndedCallbackError: () => finishAdAttempt("Fluid Player: VAST video ended with error"),
                vastVideoFailedCallback: () => finishAdAttempt("Fluid Player: VAST video failed"),
              },
            }
            : undefined,
        });

        if (adUrl) {
          const skipTimer = setTimeout(() => {
            if (!cancelled && !adDoneRef.current) setCanSkipAd(true);
          }, 6000);

          const failTimer = setTimeout(() => {
            if (!cancelled && !adDoneRef.current) {
              finishAdAttempt("Fluid Player: ad timed out before desktop creative playback");
            }
          }, 20000);

          timersRef.current = [skipTimer, failTimer];
        }
      } catch (err) {
        console.warn("Fluid Player failed to load:", err);
        onAdFinished?.();
      }
    };

    setup();

    return () => {
      cancelled = true;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      if (playerRef.current?.destroy) {
        try { playerRef.current.destroy(); } catch (_) { }
      }
      playerRef.current = null;
    };
  }, [adUrl, onAdFinished, videoUrl]);

  return (
    <div className={`fluid-player-shell relative w-full h-full max-h-[calc(100vh-56px)] bg-black ${forceMobileSlot ? "fluid-player-mobile-slot" : ""}`}>
      <video
        id={videoIdRef.current}
        className="w-full h-full"
        playsInline
      >
        <source src={videoUrl} type={getSourceType(videoUrl)} />
      </video>
    </div>
  );
}
